/**
 * ResetLeadConfirm.tsx — the combined "reset lead" destructive-action
 * confirmation panel (story E0-BO-CONV11C, ADR-079 D4/D5).
 *
 * Rewires the old CONV4/CONV8 gated per-thread reset control: this is now the
 * operator's SOLE reset trigger (AC1/AC6) — no separate "funnel only" or
 * "conversation only" control exists. Mirrors the Numbers panel's
 * `ClearNumberConfirm` destructive-action shape (AC3): a visually distinct
 * (status-down ring) confirm step, focus moved on open (WCAG 2.4.3), an
 * optional free-text reason (AC4, never required).
 *
 * AC2 — the sharpest edge case: a funnel/conversation half can fail
 * independently (no shared transaction, D4 explicitly rejects a saga). The
 * result view renders `funnel_reset` / `conversation_reset` as two
 * DISTINGUISHABLE outcomes, never collapsed into one generic "reset failed"
 * message and never a silent partial reported as full success. Only a true
 * HTTP-level outage (404 unknown lead / 502 upstream down) is caught as an
 * `ApiError` — every other outcome, including the funnel 409-conflict
 * no-op-abort, arrives as a 200 `ResetLeadResponse` rendered off its two
 * `*_reset` fields, never off HTTP status alone.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import type { ResetLeadHalfResult, ResetLeadResponse } from '../../api/types';

export interface ResetLeadConfirmProps {
  apiClient: ApiClient;
  threadId: string;
  leadId: string;
  /** Called after the operator dismisses a completed (success or partial) result. */
  onDone: () => void;
  onCancel: () => void;
}

type Phase = 'confirm' | 'submitting' | 'result' | 'httpError';

const HALF_STYLE: Record<ResetLeadHalfResult, string> = {
  succeeded: 'bg-status-ok/15 text-status-ok ring-status-ok/40',
  failed: 'bg-status-down/15 text-status-down ring-status-down/40',
  skipped: 'bg-status-degraded/15 text-status-degraded ring-status-degraded/40',
};

const HalfBadge = ({
  testid,
  label,
  result,
}: {
  testid: string;
  label: string;
  result: ResetLeadHalfResult;
}) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testid}
      data-result={result}
      className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-meta ring-1 ring-inset ${HALF_STYLE[result]}`}
    >
      <span>{label}</span>
      <span className="font-semibold uppercase tracking-wide">
        {t(`conversations.detail.resetLead.half.${result}`)}
      </span>
    </div>
  );
};

export const ResetLeadConfirm = ({
  apiClient,
  threadId,
  leadId,
  onDone,
  onCancel,
}: ResetLeadConfirmProps) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ResetLeadResponse | null>(null);
  const [httpErrorDetail, setHttpErrorDetail] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus moves to the panel on open (WCAG 2.4.3), same as ClearNumberConfirm.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const handleConfirm = async () => {
    setPhase('submitting');
    setUnauthorized(false);
    setHttpErrorDetail(null);
    const trimmed = reason.trim();
    try {
      const response = await apiClient.resetLead(threadId, {
        lead_id: leadId,
        reason: trimmed || undefined,
      });
      setResult(response);
      setPhase('result');
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        setUnauthorized(true);
        setPhase('httpError');
        return;
      }
      // Only true outages (404 unknown lead / 502 upstream down) reach here —
      // every other outcome is a 200 ResetLeadResponse handled above.
      const detail = error instanceof ApiError ? error.detail : null;
      setHttpErrorDetail(detail);
      setPhase('httpError');
    }
  };

  if (phase === 'result' && result != null) {
    return (
      <div
        ref={panelRef}
        tabIndex={-1}
        role="group"
        aria-label={t('conversations.detail.resetLead.resultHeading')}
        data-testid="conv-reset-lead-result"
        className="flex flex-col gap-3 rounded-lg bg-surface-1 p-4 ring-2 ring-inset ring-ink-3/20 focus:outline-none"
      >
        <h2 className="text-h3 text-ink-1">
          {result.success
            ? t('conversations.detail.resetLead.resultSuccess')
            : t('conversations.detail.resetLead.resultPartial')}
        </h2>
        {!result.success ? (
          <p role="alert" className="text-body text-status-down">
            {t('conversations.detail.resetLead.resultPartialBody')}
          </p>
        ) : null}
        <div className="flex flex-col gap-2">
          <HalfBadge
            testid="conv-reset-lead-funnel-result"
            label={t('conversations.detail.resetLead.funnelHalf')}
            result={result.funnel_reset}
          />
          <HalfBadge
            testid="conv-reset-lead-conversation-result"
            label={t('conversations.detail.resetLead.conversationHalf')}
            result={result.conversation_reset}
          />
        </div>
        {result.funnel_error != null ? (
          <p data-testid="conv-reset-lead-funnel-error" className="text-meta text-ink-2">
            {t('conversations.detail.resetLead.errorDetail', { detail: result.funnel_error })}
          </p>
        ) : null}
        {result.conversation_error != null ? (
          <p data-testid="conv-reset-lead-conversation-error" className="text-meta text-ink-2">
            {t('conversations.detail.resetLead.errorDetail', { detail: result.conversation_error })}
          </p>
        ) : null}
        <button
          type="button"
          data-testid="conv-reset-lead-close"
          onClick={onDone}
          className="inline-flex w-fit items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3"
        >
          {t('conversations.detail.resetLead.close')}
        </button>
      </div>
    );
  }

  const submitting = phase === 'submitting';

  const httpErrorMessage = (): string => {
    if (unauthorized) return t('conversations.detail.resetLead.unauthorized');
    if (httpErrorDetail != null) {
      return t('conversations.detail.controlDetail', { detail: httpErrorDetail });
    }
    return t('conversations.detail.resetLead.httpError');
  };

  const onConfirmClick = (): void => {
    handleConfirm().catch(() => undefined);
  };

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="group"
      aria-label={t('conversations.detail.resetLead.confirmHeading')}
      data-testid="conv-reset-lead-confirm"
      className="flex flex-col gap-3 rounded-lg bg-surface-1 p-4 ring-2 ring-inset ring-status-down/60 focus:outline-none focus:ring-2 focus:ring-status-down"
    >
      <h2 className="text-h3 text-status-down">
        {t('conversations.detail.resetLead.confirmHeading')}
      </h2>
      <p className="text-body text-ink-1">{t('conversations.detail.resetLead.confirmBody')}</p>

      <label className="flex flex-col gap-1 text-meta text-ink-2" htmlFor="conv-reset-lead-reason">
        {t('conversations.detail.resetLead.reasonLabel')}
      </label>
      <textarea
        id="conv-reset-lead-reason"
        data-testid="conv-reset-lead-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={submitting}
        rows={2}
        className="rounded-lg bg-surface-2 px-3 py-2 text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus:outline-none focus:ring-2 focus:ring-outline-focus"
      />

      {phase === 'httpError' ? (
        <p
          role="alert"
          data-testid="conv-reset-lead-http-error"
          className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
        >
          {httpErrorMessage()}
        </p>
      ) : null}

      <div className="flex gap-3">
        <button
          type="button"
          data-testid="conv-reset-lead-confirm-submit"
          disabled={submitting}
          aria-busy={submitting}
          onClick={onConfirmClick}
          className="inline-flex items-center rounded-lg bg-status-down px-4 py-1.5 text-body font-medium text-canvas hover:bg-status-down/90 disabled:opacity-60"
        >
          {submitting
            ? t('conversations.detail.resetLead.submitting')
            : t('conversations.detail.resetLead.confirmSubmit')}
        </button>
        <button
          type="button"
          data-testid="conv-reset-lead-cancel"
          disabled={submitting}
          onClick={onCancel}
          className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('conversations.detail.resetLead.cancel')}
        </button>
      </div>
    </div>
  );
};
